import React, { useState, useEffect } from 'react';
import api from '../../services/api';
import { useAuth } from '../../context/AuthContext';

export default function ShippingSection({ order, onShipmentCreated, onWaybillUpdated }) {
  const { isPrivileged } = useAuth();
  const [mode, setMode] = useState('idle'); // 'idle' | 'manual' | 'delhivery'
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [waybillForm, setWaybillForm] = useState({ waybill_number: '', waybill_date: '' });
  const [delhiveryFields, setDelhiveryFields] = useState({ pincode: '', weight: '0.5' });

  useEffect(() => {
    if (order.waybill_number) {
      setWaybillForm({
        waybill_number: order.waybill_number,
        waybill_date: order.waybill_date ? order.waybill_date.split('T')[0] : '',
      });
    }
  }, [order.waybill_number, order.waybill_date]);

  const handleManualSave = async () => {
    if (!waybillForm.waybill_number.trim()) {
      setError('Waybill number is required.');
      return;
    }
    setLoading(true); setError('');
    try {
      const { data } = await api.patch(`/orders/${order.id}/waybill`, {
        waybill_number: waybillForm.waybill_number.trim(),
        waybill_date: waybillForm.waybill_date || undefined,
      });
      onWaybillUpdated && onWaybillUpdated(data.data);
      setMode('idle');
    } catch (e) {
      setError(e.response?.data?.message || 'Failed to save waybill.');
    } finally { setLoading(false); }
  };

  const handleDelhivery = async () => {
    setLoading(true); setError('');
    try {
      const { data } = await api.post(`/orders/${order.id}/shipment`, delhiveryFields);
      onShipmentCreated && onShipmentCreated(data);
      setMode('idle');
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to create shipment.');
    } finally { setLoading(false); }
  };

  // ── Waybill already set ──────────────────────────────────────────────────
  if (order.waybill_number && mode === 'idle') {
    return (
      <div style={{ marginTop: 12, padding: '10px 14px', background: '#f0fdf4', border: '1px solid #86efac', borderRadius: 8 }}>
        <div style={{ fontWeight: 600, fontSize: '0.82rem', color: '#15803d', marginBottom: 4 }}>
          Waybill
        </div>
        <div style={{ fontSize: '0.78rem', color: '#166534', display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <span>No: <strong>{order.waybill_number}</strong></span>
          {order.waybill_date && (
            <span style={{ background: '#dcfce7', padding: '1px 8px', borderRadius: 10 }}>
              {new Date(order.waybill_date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
            </span>
          )}
          {order.shipping_status && (
            <span style={{ background: '#dcfce7', padding: '1px 8px', borderRadius: 10 }}>
              {order.shipping_status}
            </span>
          )}
          {order.tracking_url && (
            <a href={order.tracking_url} target="_blank" rel="noopener noreferrer"
              style={{ color: '#0891b2', fontWeight: 600, textDecoration: 'none' }}>
              Track &rarr;
            </a>
          )}
        </div>
        {isPrivileged && (
          <button
            className="btn btn-sm btn-outline-secondary"
            style={{ marginTop: 8, fontSize: '0.75rem' }}
            onClick={() => setMode('manual')}
          >
            Edit Waybill
          </button>
        )}
      </div>
    );
  }

  if (!isPrivileged) return null;

  // ── Choose mode ──────────────────────────────────────────────────────────
  if (mode === 'idle') {
    return (
      <div style={{ marginTop: 12, padding: '10px 14px', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8 }}>
        <div style={{ fontWeight: 600, fontSize: '0.82rem', color: '#475569', marginBottom: 8 }}>Add Waybill / E-Way Bill</div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn btn-sm btn-outline-primary" style={{ fontSize: '0.8rem' }} onClick={() => setMode('manual')}>
            Enter Manually
          </button>
          {order.order_type === 'purchase_order' || order.order_type === 'invoice' ? (
            <button className="btn btn-sm btn-outline-secondary" style={{ fontSize: '0.8rem' }} onClick={() => setMode('delhivery')}>
              Generate via Delhivery
            </button>
          ) : null}
        </div>
      </div>
    );
  }

  // ── Manual entry form ────────────────────────────────────────────────────
  if (mode === 'manual') {
    return (
      <div style={{ marginTop: 12, padding: '10px 14px', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8 }}>
        <div style={{ fontWeight: 600, fontSize: '0.82rem', color: '#475569', marginBottom: 8 }}>
          {order.waybill_number ? 'Edit Waybill' : 'Enter Waybill Details'}
        </div>
        <div style={{ display: 'flex', gap: 8, marginBottom: 8, flexWrap: 'wrap' }}>
          <input
            className="form-control"
            placeholder="Waybill / E-Way Bill No."
            value={waybillForm.waybill_number}
            onChange={e => setWaybillForm(p => ({ ...p, waybill_number: e.target.value }))}
            style={{ flex: 1, minWidth: 160, fontSize: '0.82rem' }}
          />
          <input
            className="form-control"
            type="date"
            value={waybillForm.waybill_date}
            onChange={e => setWaybillForm(p => ({ ...p, waybill_date: e.target.value }))}
            style={{ width: 150, fontSize: '0.82rem' }}
          />
        </div>
        {error && <div style={{ fontSize: '0.78rem', color: '#dc2626', marginBottom: 6 }}>{error}</div>}
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn btn-sm btn-primary" onClick={handleManualSave} disabled={loading}>
            {loading ? 'Saving…' : 'Save'}
          </button>
          <button className="btn btn-sm btn-outline-secondary" onClick={() => { setMode('idle'); setError(''); }}>
            Cancel
          </button>
        </div>
      </div>
    );
  }

  // ── Delhivery form ───────────────────────────────────────────────────────
  if (mode === 'delhivery') {
    return (
      <div style={{ marginTop: 12, padding: '10px 14px', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8 }}>
        <div style={{ fontWeight: 600, fontSize: '0.82rem', color: '#475569', marginBottom: 8 }}>Create Shipment (Delhivery)</div>
        <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
          <input className="form-control" placeholder="Pincode" value={delhiveryFields.pincode}
            onChange={e => setDelhiveryFields(p => ({ ...p, pincode: e.target.value }))}
            style={{ flex: 1, fontSize: '0.82rem' }} />
          <input className="form-control" placeholder="Weight (kg)" type="number" step="0.1" value={delhiveryFields.weight}
            onChange={e => setDelhiveryFields(p => ({ ...p, weight: e.target.value }))}
            style={{ width: 110, fontSize: '0.82rem' }} />
        </div>
        {error && <div style={{ fontSize: '0.78rem', color: '#dc2626', marginBottom: 6 }}>{error}</div>}
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn btn-sm btn-primary" onClick={handleDelhivery} disabled={loading || !delhiveryFields.pincode}>
            {loading ? 'Creating…' : 'Book Shipment'}
          </button>
          <button className="btn btn-sm btn-outline-secondary" onClick={() => { setMode('idle'); setError(''); }}>
            Cancel
          </button>
        </div>
      </div>
    );
  }

  return null;
}
